/// <amd-module name="Repos/Tfvc/Views/FilesHubActions/Components/CheckinChangesPanel" />
import * as React from "react";
import { TfvcChangesetRef } from "Repos/Common/Contracts/Git";
import { getInvalidFileNameMessage } from "Repos/Common/Util/File";
import { ITfvcItem } from "Repos/Tfvc/Views/FilesHub/FilesHubViewService";
import * as Resources from "Repos/Tfvc/Views/FilesHubActions/Resources";
import { IObservableValue, IReadonlyObservableArray, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IRegionComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { FileInput, FileInputResult, FileInputUpdateEventData } from "VSSUI/FileInput";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { ITextField, TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { KeyCode } from "VSSUI/Util";

export interface ICheckinChangesDetails {
    comment: string;
    files?: FileInputResult[];
    newFolderPath?: string;
    renameTo?: string;
}

export interface GatedCheckinResponse {
    isGatedCheckin: boolean;

    affectedBuildDefinitionUris: string[];
    affectedBuildDefinitionNames: string[];
    shelvesetName: string;
    buildId: number;
    buildUri: string;
}

export interface ICheckinChangesContext {
    addCheckinCompleteListener: (listener: CheckinCompleteListener) => void;
}

export declare type CheckinCompleteListener = (changesetRef: TfvcChangesetRef) => void | Promise<void>;

export interface ICheckinChangesPanelProps {
    comment: string;
    checkinChangesContext?: ICheckinChangesContext;
    errorMessage: IReadonlyObservableValue<string>;
    gatedCheckin: IObservableValue<GatedCheckinResponse | undefined>;
    initialFiles?: FileList;
    renameFrom?: string;
    newFolderParentPath?: string;
    showOverlay: IReadonlyObservableValue<boolean>;
    uploadExistingItems?: IReadonlyObservableArray<ITfvcItem>;
    uploadTo?: string;
    onDismiss: (details?: ICheckinChangesDetails) => void;
}

// Set max file size to edit at 20 MB. The maximum size of the payload on the server is 25 MB. Use something
// less to account for JSON payload overhead + allowing for some unicode characters (transferred as multiple bytes).
// This is not exact. We may be enforcing a slightly lower limit here than what would normally be allowed.
// We could also not detect this state if the file contained a whole bunch of unicode text. That's okay, in that
// case the server will reject the request. This just helps us have a better experience where we fail earlier and
// give a better error message.
const maxFileContentSize = 20 * 1024 * 1024;

export class CheckinChangesPanel extends VssComponent<ICheckinChangesPanelProps> {
    private renameAs: ObservableValue<string> | undefined;
    private comment: ObservableValue<string>;
    private fieldsValid: ObservableValue<boolean>;
    private newFolderName: ObservableValue<string> | undefined;

    private newFolderNameMessage = new ObservableValue<string>(Resources.NewFileNameDefaultMessage);
    private newFolderNameMessageIsError = new ObservableValue<boolean>(false);

    private renameAsError = new ObservableValue(false);
    private renameAsErrorMessage = new ObservableValue("");

    private commentError = new ObservableValue(false);
    private commentErrorMessage = new ObservableValue("");

    private userChangedComment = false;

    private uploadData: FileInputUpdateEventData;
    private updateExistingFiles: { [name: string]: ITfvcItem } = {};

    constructor(props: ICheckinChangesPanelProps) {
        super(props);

        if (props.renameFrom) {
            this.renameAs = new ObservableValue(props.renameFrom);
        }

        if (props.newFolderParentPath) {
            this.newFolderName = new ObservableValue<string>("");
        }

        this.comment = new ObservableValue(props.comment);

        this.fieldsValid = new ObservableValue(!props.newFolderParentPath && !props.renameFrom && !props.uploadTo);

        if (this.props.uploadExistingItems) {
            this.updateExistingItemsHash();
            this.observe([
                {
                    propertyName: "uploadExistingItems",
                    observer: () => {
                        this.updateExistingItemsHash();
                        this.updateFileInputComment();
                        this.validate();
                    }
                }
            ]);
        }
    }

    public render() {
        return (
            <Observer valid={this.fieldsValid} showOverlay={this.props.showOverlay} gatedCheckin={this.props.gatedCheckin}>
                {(props: { valid: boolean; showOverlay: boolean; gatedCheckin?: GatedCheckinResponse }) => {
                    const { onDismiss } = this.props;
                    const buttons: IButtonProps[] = props.gatedCheckin
                        ? []
                        : [
                              {
                                  onClick: () => onDismiss(),
                                  text: Resources.Cancel
                              },
                              {
                                  onClick: this.submit,
                                  disabled: !props.valid,
                                  primary: true,
                                  text: Resources.Checkin
                              }
                          ];

                    return (
                        <Panel
                            defaultActiveElement={
                                this.props.renameFrom
                                    ? ".repos-checkin-panel-rename-input"
                                    : this.props.newFolderParentPath
                                    ? ".repos-checkin-panel-new-folder-input"
                                    : ".repos-checkin-panel-comment-input"
                            }
                            lightDismiss={false}
                            onDismiss={() => onDismiss()}
                            overlayContent={props.showOverlay ? <Spinner /> : undefined}
                            titleProps={{ text: Resources.Checkin }}
                            footerButtonProps={buttons}
                        >
                            <div className="rhythm-vertical-16 flex-grow">
                                <Observer errorMessage={this.props.errorMessage}>
                                    {(props: { errorMessage: string }) => {
                                        return (
                                            props.errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>
                                        );
                                    }}
                                </Observer>
                                {this.renameAs && (
                                    <FormItem label={Resources.RenameAs} error={this.renameAsError} message={this.renameAsErrorMessage}>
                                        <TextField
                                            ref={this.resolveRenameRef}
                                            onChange={this.onRenameAsChanged}
                                            onKeyDown={this.onRenameAsKeyDown}
                                            value={this.renameAs}
                                            inputClassName="repos-checkin-panel-rename-input"
                                        />
                                    </FormItem>
                                )}
                                {this.props.uploadTo && (
                                    <FileInput
                                        fileNamesCaseSensitive={true}
                                        initialFiles={this.props.initialFiles}
                                        limitMessageFormatter={message => message + Resources.FileSizeLimitWorkaround}
                                        maximumTotalFileSize={maxFileContentSize}
                                        updateHandler={this.onFileInputUpdated}
                                    />
                                )}
                                {this.props.newFolderParentPath && (
                                    <FormItem
                                        label={Resources.NewFolderName}
                                        error={this.newFolderNameMessageIsError}
                                        message={this.newFolderNameMessage}
                                    >
                                        <TextField
                                            onChange={this.onNewFolderNameChanged}
                                            prefixIconProps={{
                                                render: () => (
                                                    <span className="repos-new-file-dialog-path-prefix flex-row flex-center">
                                                        <Tooltip overflowOnly={true} text={this.props.newFolderParentPath}>
                                                            <span className="text-ellipsis">{this.props.newFolderParentPath}</span>
                                                        </Tooltip>
                                                    </span>
                                                )
                                            }}
                                            value={this.newFolderName}
                                            inputClassName="repos-checkin-panel-new-folder-input"
                                        />
                                    </FormItem>
                                )}
                                <FormItem label={Resources.Comment} error={this.commentError} message={this.commentErrorMessage}>
                                    <TextField
                                        disabled={Boolean(props.gatedCheckin)}
                                        onChange={this.onCommentChanged}
                                        multiline={true}
                                        value={this.comment}
                                        inputClassName="repos-checkin-panel-comment-input"
                                        rows={5}
                                    />
                                </FormItem>
                                {this.props.checkinChangesContext && (
                                    <WrappedComponent<IRegionComponentProperties>
                                        dependencies={["ms.vss-code-web.checkin-changes-panel-extensions"]}
                                        wrappedType="ms.vss-web.region"
                                        wrappedProps={{
                                            componentRegion: "checkinChangesPanelExtensions",
                                            childComponentProperties: { checkinChangesContext: this.props.checkinChangesContext }
                                        }}
                                    />
                                )}
                                {props.gatedCheckin &&
                                    (props.gatedCheckin.buildId ? (
                                        <MessageCard severity={MessageCardSeverity.Info}>
                                            {format(
                                                Resources.QueuedGatedCheckin,
                                                props.gatedCheckin.shelvesetName,
                                                props.gatedCheckin.affectedBuildDefinitionNames[0]
                                            )}
                                        </MessageCard>
                                    ) : (
                                        <MessageCard severity={MessageCardSeverity.Warning}>
                                            <span>{Resources.RequiredGatedCheckin}</span>
                                            <ul>
                                                {props.gatedCheckin.affectedBuildDefinitionNames.map((name, index) => (
                                                    <li key={index}>{name}</li>
                                                ))}
                                            </ul>
                                        </MessageCard>
                                    ))}
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private submit = (): void => {
        const details = {
            comment: this.comment.value,
            files: this.uploadData ? this.uploadData.files.map(f => f.result) : undefined,
            newFolderPath: this.newFolderName ? this.newFolderName.value : undefined,
            renameTo: this.renameAs ? this.renameAs.value : undefined
        };

        this.props.onDismiss(details);
    };

    private resolveRenameRef = (renameTextField: ITextField | null) => {
        if (renameTextField) {
            renameTextField.focus();

            const lastDot = this.props.renameFrom!.lastIndexOf(".");
            if (lastDot > 0) {
                renameTextField.setSelectionRange(0, lastDot);
            } else {
                renameTextField.select();
            }
        }
    };

    private onNewFolderNameChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string): void => {
        this.newFolderName!.value = name;

        if (!this.userChangedComment) {
            this.comment.value = format(Resources.AddedFileToCommentFormat, name, this.props.newFolderParentPath);
        }

        let message = Resources.NewFileNameDefaultMessage;
        let messageIsError = false;

        name = name.trim();
        if (!name || name.endsWith("/")) {
            message = Resources.NewFileNameCannotBeEmpty;
            messageIsError = true;
        } else {
            const parts = name.split("/");
            let path = "/";
            for (const part of parts) {
                if (!part.trim()) {
                    message = format(Resources.NewFileFolderNameEmptyFormat, path);
                    messageIsError = true;
                    break;
                }

                const invalidNameMessage = getInvalidFileNameMessage(part);
                if (invalidNameMessage) {
                    message = invalidNameMessage;
                    messageIsError = true;
                    break;
                }
                path += part + "/";
            }
        }

        this.newFolderNameMessage.value = message;
        this.newFolderNameMessageIsError.value = messageIsError;

        this.validate();
    };

    private onRenameAsChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, renameAs: string): void => {
        this.renameAs!.value = renameAs;

        if (!this.userChangedComment) {
            this.comment.value = format(Resources.RenameCommentFormat, this.props.renameFrom, renameAs);
        }

        let valid = true;
        if (!renameAs) {
            this.renameAsErrorMessage.value = Resources.RenameAsCannotBeEmpty;
            valid = false;
        } else {
            const invalidFileNameMessage = getInvalidFileNameMessage(renameAs);
            if (invalidFileNameMessage) {
                this.renameAsErrorMessage.value = invalidFileNameMessage;
                valid = false;
            } else if (renameAs === this.props.renameFrom) {
                this.renameAsErrorMessage.value = Resources.RenameAsDifferentName;
                valid = false;
            } else {
                this.renameAsErrorMessage.value = "";
            }
        }

        this.renameAsError.value = !valid;
        this.validate();
    };

    private onRenameAsKeyDown = (ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (ev.which === KeyCode.enter && this.fieldsValid.value) {
            this.submit();
        }
    };

    private onCommentChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, comment: string): void => {
        this.comment.value = comment;
        this.userChangedComment = true;

        let valid = true;
        if (!comment) {
            this.commentErrorMessage.value = Resources.CommentCannotBeEmpty;
            valid = false;
        } else {
            this.commentErrorMessage.value = "";
        }

        this.commentError.value = !valid;

        this.validate();
    };

    private onFileInputUpdated = (updateEvent: FileInputUpdateEventData): void => {
        this.uploadData = updateEvent;
        this.updateFileInputComment();
        this.validate();
    };

    private updateExistingItemsHash(): void {
        this.updateExistingFiles = {};
        for (const item of this.props.uploadExistingItems!.value) {
            if (!item.isFolder) {
                this.updateExistingFiles[item.name.toLocaleLowerCase()] = item;
            }
        }
    }

    private updateFileInputComment(): void {
        const uploadData = this.uploadData;
        if (uploadData && !this.userChangedComment) {
            if (uploadData.files.length === 0) {
                this.comment.value = "";
            } else {
                const hasUpdates = this.uploadData.files.some(f => this.updateExistingFiles[f.result.name.toLocaleLowerCase()]);
                if (uploadData.files.length === 1) {
                    if (hasUpdates) {
                        this.comment.value = format(Resources.UpdatedFileCommentFormat, uploadData.files[0].result.name);
                    } else {
                        this.comment.value = format(Resources.AddedFileToCommentFormat, uploadData.files[0].result.name, this.props.uploadTo);
                    }
                } else {
                    this.comment.value = format(
                        hasUpdates ? Resources.UpdatedFilesCommentFormat : Resources.AddedFilesCommentFormat,
                        uploadData.files.length,
                        this.props.uploadTo
                    );
                }
            }
        }
    }

    private validate(): void {
        let valid = !this.renameAsError.value && !this.newFolderNameMessageIsError.value && !this.commentError.value && !!this.comment.value;
        if (valid && this.props.uploadTo) {
            valid =
                this.uploadData && !this.uploadData.loading && this.uploadData.files.length > 0 && !this.uploadData.files.some(f => f.result.isError);
        }

        if (valid !== this.fieldsValid.value) {
            this.fieldsValid.value = valid;
        }
    }
}
